/**
 * Buscador global ⌘K (GEN-EKB-034). Campo en la barra superior y panel de resultados agrupados debajo.
 * Una sola llamada (`buscarSaber`) devuelve los grupos; aquí cada grupo es un dato de GRUPOS, de modo que
 * GEN-PX-E08 añadirá chats y personas con una entrada más, sin tocar el panel.
 * Abrir un resultado es cambiar el hash: AppShell ya sabe abrir `#/saber/iku|kb|area/<id>` desde cualquier zona.
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Search } from 'lucide-react'
import { buscarSaber } from '../api/client.js'
import { PREFIJO_CITA_IKU, PREFIJO_CITA_KB, PREFIJO_AREA } from '../lib/citaIku.js'

export const AYUDA = 'Buscar en lo que sabe la empresa: KBs, iKus, entidades…'
export const DEBOUNCE_MS = 250
export const MIN_CARACTERES = 2

const GRUPOS = [
  { clave: 'ikus',  titulo: 'iKus',  id: r => r.iku_id, etiqueta: r => r.titulo,  detalle: r => r.fragmento, ruta: r => PREFIJO_CITA_IKU + r.iku_id },
  { clave: 'kbs',   titulo: 'KBs',   id: r => r.id,     etiqueta: r => r.nombre,  detalle: () => '',        ruta: r => PREFIJO_CITA_KB + r.id },
  { clave: 'areas', titulo: 'Áreas', id: r => r.id,     etiqueta: r => r.nombre,  detalle: r => (r.ruta ?? []).join(' › '), ruta: r => PREFIJO_AREA + r.id },
]

const esMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '')

function abrirRuta(ruta) {
  if (window.location.hash === ruta) window.dispatchEvent(new HashChangeEvent('hashchange'))
  else window.location.hash = ruta
}

export default function BuscadorGlobal() {
  const [q, setQ] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [estado, setEstado] = useState('idle') // idle | cargando | listo | error
  const [datos, setDatos] = useState(null)
  const [activo, setActivo] = useState(0)
  const ref = useRef(null)
  const campo = useRef(null)
  const peticion = useRef(0) // ignora las respuestas tardías

  const texto = q.trim()
  const buscable = texto.length >= MIN_CARACTERES

  useEffect(() => {
    const tecla = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        campo.current?.focus()
        setAbierto(true)
      }
    }
    document.addEventListener('keydown', tecla)
    return () => document.removeEventListener('keydown', tecla)
  }, [])

  useEffect(() => {
    if (!abierto) return
    const fuera = e => { if (!ref.current?.contains(e.target)) setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto])

  useEffect(() => {
    const n = ++peticion.current
    if (!buscable) { setEstado('idle'); setDatos(null); return undefined }
    setEstado('cargando')
    const t = setTimeout(() => {
      buscarSaber(texto)
        .then(r => { if (n === peticion.current) { setDatos(r); setActivo(0); setEstado('listo') } })
        .catch(() => { if (n === peticion.current) { setDatos(null); setEstado('error') } })
    }, DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [texto, buscable])

  const grupos = useMemo(
    () => GRUPOS.map(g => ({ ...g, items: (datos?.[g.clave] ?? []) })).filter(g => g.items.length),
    [datos],
  )
  const planos = useMemo(() => grupos.flatMap(g => g.items.map(it => ({ g, it }))), [grupos])

  const cerrar = useCallback(() => { setAbierto(false) }, [])
  const abrir = useCallback(({ g, it }) => {
    abrirRuta(g.ruta(it))
    setAbierto(false); setQ('')
    campo.current?.blur()
  }, [])

  const alTeclear = e => {
    if (e.key === 'Escape') { e.preventDefault(); cerrar(); campo.current?.blur() }
    else if (e.key === 'ArrowDown' && planos.length) { e.preventDefault(); setActivo(a => (a + 1) % planos.length) }
    else if (e.key === 'ArrowUp' && planos.length) { e.preventDefault(); setActivo(a => (a - 1 + planos.length) % planos.length) }
    else if (e.key === 'Enter' && estado === 'listo' && planos[activo]) { e.preventDefault(); abrir(planos[activo]) }
  }

  const mostrarPanel = abierto && buscable
  let indice = -1
  return (
    <div className="dk-buscador" ref={ref}>
      <div className="dk-buscador__campo">
        <Search size={15} strokeWidth={1.8} aria-hidden="true" />
        <input
          ref={campo} type="text" value={q} placeholder={AYUDA} autoComplete="off" spellCheck={false}
          role="combobox" aria-label="Buscar en Saber" aria-expanded={mostrarPanel} aria-controls="dk-buscador-lista"
          aria-autocomplete="list" aria-activedescendant={mostrarPanel && estado === 'listo' && planos.length ? `dk-buscador-op-${activo}` : undefined}
          onChange={e => { setQ(e.target.value); setAbierto(true) }} onFocus={() => setAbierto(true)} onKeyDown={alTeclear}
        />
        <kbd aria-hidden="true">{esMac() ? '⌘K' : 'Ctrl K'}</kbd>
      </div>
      {mostrarPanel && (
        <div className="dk-buscador__panel" id="dk-buscador-lista" role="listbox" aria-label="Resultados de la búsqueda">
          {estado === 'cargando' && <p className="dk-buscador__estado" role="status">Buscando…</p>}
          {estado === 'error' && <p className="dk-buscador__estado" role="alert">No se pudo buscar. Inténtalo de nuevo.</p>}
          {estado === 'listo' && !planos.length && <p className="dk-buscador__estado" role="status">Sin resultados para «{texto}»</p>}
          {estado === 'listo' && grupos.map(g => (
            <div key={g.clave} role="group" aria-label={g.titulo}>
              <span className="dk-buscador__grupo">{g.titulo}</span>
              {g.items.map(it => {
                indice += 1
                const i = indice
                const detalle = g.detalle(it)
                return (
                  <button
                    key={g.id(it)} type="button" role="option" id={`dk-buscador-op-${i}`} aria-selected={i === activo}
                    className={'dk-buscador__op' + (i === activo ? ' dk-buscador__op--activa' : '')}
                    onMouseEnter={() => setActivo(i)} onClick={() => abrir({ g, it })}
                  >
                    <span className="dk-buscador__titulo">{g.etiqueta(it)}</span>
                    {detalle && <span className="dk-buscador__detalle">{detalle}</span>}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
